import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import type { SessionDto } from '@/types/dto';
import { Card, CardTitle, CardDescription } from '@/components/ui/card';
import { ThemeSwitcher } from '@/components/shell/ThemeSwitcher';

export default function Login() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const returnUrl = params.get('returnUrl') ?? '/';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => api.post<void>('/api/login', { username, password }),
    onSuccess: async () => {
      // After /api/login sets the auth cookie, fetch the session OURSELVES and seed the cache
      // BEFORE navigating, so RequireAuth reads authenticated:true on its first render. The
      // ['session'] query is unmounted on /login, and invalidateQueries does not reliably refetch+
      // await an inactive query — that left the stale {authenticated:false} in cache, so RequireAuth
      // bounced the first login straight back to /login (fields cleared; second attempt worked).
      // setQueryData on the freshly-fetched session is deterministic — no refetch-timing dependency.
      try {
        const session = await api.get<SessionDto>('/api/session');
        qc.setQueryData(['session'], session);
      } catch {
        await qc.invalidateQueries({ queryKey: ['session'], refetchType: 'all' });
      }
      nav(returnUrl, { replace: true });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 429) {
        setError('Too many attempts — wait 15 minutes and retry.');
      } else {
        setError('Invalid username or password.');
      }
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    submit.mutate();
  };

  // Submit explicitly on Enter. Browser implicit submission was unreliable here (a hidden
  // first submit button shadowed the real one as the form's default button). requestSubmit()
  // runs field validation and fires onSubmit directly, independent of the default-button.
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-end p-4">
        <ThemeSwitcher />
      </header>
      <div className="flex-1 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="h-6 w-6 text-accent" />
            <CardTitle className="text-xl">HMH Apps</CardTitle>
          </div>
          <CardDescription className="mb-6">
            Sign in to continue. This is an internal tool.
          </CardDescription>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">Username</span>
              <input
                type="text"
                autoComplete="username"
                autoFocus
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={onKeyDown}
                className="bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11 focus:outline-2 focus:outline-offset-2 focus:outline-accent"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={onKeyDown}
                className="bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11 focus:outline-2 focus:outline-offset-2 focus:outline-accent"
              />
            </label>
            {error && (
              <p role="alert" className="text-sm text-accent-danger border-l-2 border-accent-danger pl-3 py-1">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submit.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-(--radius-sm) font-(family-name:--font-ui) font-semibold uppercase tracking-[0.1em] text-sm min-h-12 px-6 py-3 bg-accent text-inverse border border-accent hover:bg-accent-hover hover:border-accent-hover transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submit.isPending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
}
